import React from "react";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
} from "reactstrap";
import { connect } from "react-redux";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import RowActions, {
  deleteAction,
} from "components/Table/RowActions";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";
import { FormModal } from "../../components/Form/FormKit";
import UserAccessForm from "./UserAccessForm";
import PageHeader from "../../components/Page/PageHeader";

class UserAccessList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      selectedMerchant: "",
      formOpen: false,
      editing: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, this.callPage);
    } else {
      this.fetchMerchants();
    }
  }

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      const merchants = response.data.items.map((merchant) => ({
        value: merchant.id,
        label: merchant.name,
      }));
      this.setState({ isLoading: false, merchants }, () => {
        if (merchants.length === 1) {
          this.setState({ selectedMerchant: merchants[0].value }, this.callPage);
        }
      });
    }
  };

  callPage = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) {
      this.setState({ isLoading: false });
      return;
    }

    this.setState({ isLoading: true });
    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/users`
    );
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        dataSource: response.data.items.map((item, key) => ({
          id: key,
          data: item,
          name: `${item.user.firstName || ""} ${item.user.lastName || ""}`.trim(),
          email: item.user.email,
          garage: item.branch ? item.branch.name || item.branch.address : "",
          role: item.role ? item.role.name : "-",
          permissions: (item.scopes || item.scope || []).length,
        })),
      });
    } else {
      this.setState({ isLoading: false });
    }
    this.props.loadingAction(false);
  };

  handleMerchantSelect = (event) => {
    this.setState({ selectedMerchant: event.value, dataSource: [] }, this.callPage);
  };

  handleAdd = () => {
    if (!this.state.selectedMerchant) {
      alert(`${strings.select} ${strings.merchant}`);
      return;
    }
    this.setState({ formOpen: true, editing: null });
  };

  handleEdit = (index) =>
    this.setState({ formOpen: true, editing: this.state.dataSource[index].data });

  handleCloseForm = () => this.setState({ formOpen: false, editing: null });

  handleSaved = () =>
    this.setState({ formOpen: false, editing: null }, this.callPage);

  handleDelete = async (item) => {
    if (!window.confirm(strings.confirmdelete)) return;
    this.props.loadingAction(true);
    const result = await this.engine.deleteItem(
      `merchants/${this.state.selectedMerchant}/users`,
      item.id
    );
    if (result) {
      this.callPage();
    }
    this.props.loadingAction(false);
  };

  handleActions = (index, item, canWrite) => (
    <RowActions
      actions={[
        {
          key: "access",
          icon: canWrite ? "fa fa-key" : "fa fa-eye",
          label: canWrite ? strings.edit_access : strings.open,
          onClick: () => this.handleEdit(index),
        },
        deleteAction(() => this.handleDelete(item), canWrite),
      ]}
    />
  );

  render() {
    const {
      dataSource,
      isLoading,
      merchants,
      selectedMerchant,
      formOpen,
      editing,
    } = this.state;
    const canWrite = this.hasPermission([Scopes.MERCHANTS_USERS_WRITE]);

    const columns = [
      { Header: strings.name, accessor: "name" },
      { Header: strings.email, accessor: "email" },
      {
        Header: strings.garage,
        accessor: "garage",
        Cell: (row) =>
          row.value ? (
            <span className="access-badge access-badge--garage">
              {row.value}
            </span>
          ) : (
            <span className="access-badge">{strings.all_garages}</span>
          ),
      },
      { Header: strings.role, accessor: "role" },
      {
        Header: strings.permissions,
        accessor: "permissions",
        Cell: (row) => <span className="access-badge">{row.value}</span>,
      },
      {
        Header: strings.actions,
        accessor: "id",
        sortable: false,
        filterable: false,
        Cell: (row) =>
          this.handleActions(row.index, row.original.data, canWrite),
      },
    ];

    return (
      <div className="content">
        <PageHeader
          icon="nc-icon nc-key-25"
          title={strings.user_access}
          subtitle={strings.user_access_subtitle}
          count={dataSource.length > 0 ? dataSource.length : undefined}
        >
          {!sessionStorage.getItem("merchantId") && merchants.length !== 1 && (
            <div className="page-head__select">
              <SelectRow
                hidelabel
                name="selectedMerchant"
                defaultValue={selectedMerchant}
                data={merchants}
                changeInput={this.handleMerchantSelect}
                placeholder={strings.merchant}
              />
            </div>
          )}
          {canWrite && (
            <Button color="primary" onClick={this.handleAdd}>
              <i className="nc-icon nc-simple-add" /> {strings.add_user_access}
            </Button>
          )}
        </PageHeader>
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <ReactTable
                  previousText={strings.previous}
                  nextText={strings.next}
                  loadingText={strings.loading}
                  noDataText={strings.nodata}
                  pageText={strings.page}
                  data={dataSource}
                  defaultFilterMethod={this.filterCaseInsensitive}
                  filterable
                  loading={isLoading}
                  columns={columns}
                  defaultPageSize={10}
                  showPaginationTop={false}
                  showPaginationBottom
                  PaginationComponent={TablePagination}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <FormModal
          isOpen={formOpen}
          onClose={this.handleCloseForm}
          icon="nc-icon nc-key-25"
          title={editing ? strings.edit_access : strings.add_user_access}
          subtitle={editing ? editing.user.email : ""}
        >
          <UserAccessForm
            key={(editing && editing.id) || "new"}
            item={editing}
            merchantId={selectedMerchant}
            onSaved={this.handleSaved}
            onCancel={this.handleCloseForm}
          />
        </FormModal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(UserAccessList);
